<template>
  <header id="top" class="hero">
    <div class="container">
      <nav class="hero__nav" aria-label="Primary navigation">
        <a class="hero__brand" href="#top" aria-label="Markus Olsson, home">
          <svg
            class="hero__mark"
            viewBox="0 0 32 32"
            aria-hidden="true"
            focusable="false"
          >
            <rect width="32" height="32" rx="9"></rect>
            <circle cx="16" cy="16" r="10.25"></circle>
            <path d="M9.5 21.5V10l6.5 6.2 6.5-6.2v11.5"></path>
            <circle class="hero__mark-dot" cx="25.5" cy="6.5" r="2"></circle>
          </svg>
          <span>Markus Olsson</span>
        </a>

        <div class="hero__nav-links">
          <a href="#work">Work</a>
          <a href="#about">About</a>
          <a href="#experience">Experience</a>
          <a
            class="hero__nav-ulna"
            href="https://playulna.com/"
            target="_blank"
            rel="noopener"
          >
            Play Ulna <span aria-hidden="true">↗</span>
          </a>
        </div>
      </nav>

      <div class="hero__stage">
        <div class="hero__content">
          <p class="hero__eyebrow mono">
            Full-stack engineer · Solution architect · Malmö
          </p>

          <h1 class="hero__title">Markus Olsson</h1>
          <p class="hero__statement">
            Architecture in the large.<br />
            <span>Code in the details.</span>
          </p>

          <p class="hero__blurb">
            I design and ship .NET, web, and cloud systems across e-commerce,
            payments, CMS, and public transport. I’m also building Ulna’s Rust
            game engine and authoritative multiplayer server.
          </p>

          <div class="hero__cta">
            <a class="btn btn--primary" href="#work">
              See selected work
              <span aria-hidden="true">↓</span>
            </a>
            <a class="btn btn--ghost" href="#experience">View experience</a>
          </div>

          <ul class="hero__areas" aria-label="Areas of focus">
            <li>Enterprise .NET</li>
            <li>Cloud platforms</li>
            <li>Rust &amp; Bevy</li>
          </ul>
        </div>

        <div class="hero__visual">
          <figure class="hero__portrait">
            <div class="hero__portrait-frame">
              <img
                src="/portrait.webp"
                width="640"
                height="800"
                decoding="async"
                fetchpriority="high"
                alt="Portrait of Markus Olsson"
              />
            </div>
            <figcaption>
              <span class="mono">Based in Malmö</span>
              <strong>Engineer · Architect · Builder</strong>
            </figcaption>
          </figure>

          <a
            class="hero__now"
            href="https://playulna.com/"
            target="_blank"
            rel="noopener"
            aria-label="Visit Ulna at playulna.com"
          >
            <span class="hero__now-label">
              <span class="status-dot" aria-hidden="true"></span>
              Now building
            </span>
            <strong>Ulna</strong>
            <span>Rust · Bevy ECS · Lua · Python</span>
            <span class="hero__now-arrow" aria-hidden="true">↗</span>
          </a>
        </div>
      </div>
    </div>

    <div class="hero__rail" aria-hidden="true">
      <div class="container hero__rail-inner mono">
        <span>01 / Enterprise systems</span>
        <span>02 / Technical leadership</span>
        <span>03 / Product engineering</span>
        <span>04 / Systems programming</span>
      </div>
    </div>
  </header>
</template>

<script>
export default { name: "Header" };
</script>

<style lang="scss" scoped>
.hero {
  position: relative;
  overflow: hidden;
}

.hero::before {
  content: "";
  position: absolute;
  top: 132px;
  left: 50%;
  width: min(92vw, 1320px);
  height: 640px;
  border: 1px solid rgba(124, 226, 201, 0.08);
  border-radius: 50%;
  transform: translateX(-50%) rotate(-8deg);
  pointer-events: none;
}

.hero__nav {
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 82px;
  gap: var(--space-5);
  border-bottom: 1px solid var(--border);
}

.hero__brand {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  color: var(--text);
  font-size: 13px;
  font-weight: 720;
  letter-spacing: -0.01em;
}

.hero__brand:hover {
  text-decoration: none;
}

.hero__mark {
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
}

.hero__mark rect {
  fill: var(--surface-solid);
  stroke: var(--border-strong);
}

.hero__mark circle:not(.hero__mark-dot) {
  fill: none;
  stroke: var(--accent);
  stroke-width: 1.7;
}

.hero__mark path {
  fill: none;
  stroke: var(--text);
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2.25;
}

.hero__mark .hero__mark-dot {
  fill: var(--accent-2);
}

.hero__nav-links {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: clamp(16px, 2.4vw, 30px);
  font-size: 12px;
  font-weight: 650;
}

.hero__nav-links > a {
  color: var(--muted);
}

.hero__nav-links > a:hover {
  color: var(--text);
  text-decoration: none;
}

.hero__nav-links .hero__nav-ulna {
  padding: 9px 12px;
  border: 1px solid rgba(124, 226, 201, 0.28);
  border-radius: 999px;
  color: var(--accent-strong);
}

.hero__stage {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(270px, 350px);
  min-height: 690px;
  gap: clamp(54px, 8vw, 112px);
  align-items: center;
  padding: clamp(72px, 9vw, 112px) 0 clamp(82px, 10vw, 124px);
}

.hero__content {
  max-width: 760px;
  animation: hero-rise 700ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

.hero__eyebrow {
  margin: 0 0 var(--space-5);
  color: var(--accent);
  font-size: 11px;
  font-weight: 650;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}

.hero__title {
  max-width: 10ch;
  font-size: clamp(54px, 7vw, 88px);
  font-weight: 760;
  line-height: 0.92;
  letter-spacing: -0.065em;
}

.hero__statement {
  margin: var(--space-5) 0 0;
  color: var(--text-soft);
  font-size: clamp(25px, 3.4vw, 40px);
  font-weight: 480;
  letter-spacing: -0.035em;
  line-height: 1.15;
}

.hero__statement span {
  color: var(--muted);
}

.hero__blurb {
  max-width: 64ch;
  margin: var(--space-6) 0 0;
  color: var(--muted);
  font-size: clamp(16px, 1.55vw, 18px);
  line-height: 1.75;
}

.hero__cta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  margin-top: var(--space-6);
}

.hero__areas {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 26px;
  margin: var(--space-6) 0 0;
  padding: 0;
  color: var(--text-soft);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.07em;
  list-style: none;
  text-transform: uppercase;
}

.hero__areas li {
  display: flex;
  align-items: center;
  gap: 9px;
}

.hero__areas li::before {
  content: "";
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--accent-2);
  box-shadow: 0 0 0 4px rgba(226, 179, 102, 0.08);
}

.hero__visual {
  position: relative;
  animation: hero-rise 820ms 90ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

.hero__portrait {
  position: relative;
  margin: 0;
}

.hero__portrait-frame {
  position: relative;
  padding: 8px;
  border: 1px solid var(--border-strong);
  border-radius: 180px 180px 24px 24px;
  background: linear-gradient(180deg, rgba(124, 226, 201, 0.12), rgba(8, 13, 18, 0.7));
  box-shadow: var(--shadow);
}

.hero__portrait-frame::after {
  content: "";
  position: absolute;
  inset: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 172px 172px 17px 17px;
  pointer-events: none;
}

.hero__portrait img {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 4 / 5;
  object-fit: cover;
  border-radius: 170px 170px 16px 16px;
  filter: saturate(0.82) contrast(1.04);
}

.hero__portrait figcaption {
  display: grid;
  gap: 2px;
  padding: 14px 12px 0;
  color: var(--muted);
  font-size: 11px;
}

.hero__portrait figcaption span {
  color: var(--accent);
  font-size: 9px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.hero__portrait figcaption strong {
  color: var(--text-soft);
  font-weight: 600;
}

.hero__now {
  position: absolute;
  right: -34px;
  bottom: 52px;
  display: grid;
  min-width: 240px;
  gap: 3px;
  padding: 18px 48px 18px 20px;
  border: 1px solid rgba(124, 226, 201, 0.28);
  border-radius: var(--radius-md);
  background: rgba(8, 13, 18, 0.92);
  box-shadow: var(--shadow-soft);
  color: var(--muted);
  backdrop-filter: blur(18px);
}

.hero__now:hover {
  border-color: rgba(124, 226, 201, 0.56);
  text-decoration: none;
  transform: translateY(-2px);
}

.hero__now-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 7px;
  color: var(--accent);
  font-size: 9px;
  font-weight: 750;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}

.hero__now strong {
  color: var(--text);
  font-size: 22px;
  font-weight: 720;
  letter-spacing: -0.035em;
}

.hero__now > span:not(.hero__now-label, .hero__now-arrow) {
  font-size: 10px;
}

.hero__now-arrow {
  position: absolute;
  top: 18px;
  right: 19px;
  color: var(--accent-2);
  font-size: 18px;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 0 4px rgba(124, 226, 201, 0.1);
}

.hero__rail {
  position: relative;
  z-index: 2;
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  background: rgba(7, 11, 16, 0.48);
}

.hero__rail-inner {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.hero__rail span {
  padding: 15px clamp(10px, 2vw, 22px);
  border-left: 1px solid var(--border);
  color: var(--muted);
  font-size: 9px;
  letter-spacing: 0.08em;
  text-align: center;
  text-transform: uppercase;
}

.hero__rail span:last-child {
  border-right: 1px solid var(--border);
}

@keyframes hero-rise {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 900px) {
  .hero__stage {
    grid-template-columns: minmax(0, 1fr) 250px;
    min-height: 620px;
    gap: 44px;
  }

  .hero__now {
    right: -8px;
    bottom: 34px;
    min-width: 218px;
  }
}

@media (max-width: 700px) {
  .hero::before {
    top: 128px;
    width: 520px;
    height: 390px;
  }

  .hero__nav {
    min-height: 64px;
  }

  .hero__brand > span {
    display: none;
  }

  .hero__mark {
    width: 32px;
    height: 32px;
  }

  .hero__nav-links {
    gap: 10px;
    font-size: 10.5px;
  }

  .hero__nav-links > a {
    display: inline-flex;
    min-height: 32px;
    align-items: center;
    padding: 4px 0;
  }

  .hero__nav-links .hero__nav-ulna {
    padding: 7px 9px;
  }

  .hero__stage {
    grid-template-columns: 1fr;
    min-height: 0;
    gap: 30px;
    padding: 38px 0 52px;
  }

  .hero__visual {
    display: grid;
    grid-template-columns: 112px minmax(0, 1fr);
    gap: 12px;
    align-items: center;
    max-width: none;
  }

  .hero__portrait-frame {
    padding: 5px;
    border-radius: 120px 120px 18px 18px;
  }

  .hero__portrait-frame::after {
    inset: 5px;
    border-radius: 115px 115px 13px 13px;
  }

  .hero__portrait img {
    border-radius: 114px 114px 12px 12px;
  }

  .hero__portrait figcaption {
    display: none;
  }

  .hero__now {
    position: relative;
    right: auto;
    bottom: auto;
    min-width: 0;
    min-height: 112px;
    padding: 15px 36px 15px 16px;
  }

  .hero__eyebrow {
    margin-bottom: 18px;
    font-size: 9px;
    line-height: 1.5;
  }

  .hero__title {
    max-width: none;
    font-size: clamp(50px, 14vw, 58px);
  }

  .hero__statement {
    margin-top: 20px;
    font-size: clamp(24px, 7.4vw, 30px);
  }

  .hero__blurb {
    margin-top: 24px;
    font-size: 15px;
    line-height: 1.65;
  }

  .hero__cta {
    gap: 10px;
    margin-top: 26px;
  }

  .hero__areas,
  .hero__rail {
    display: none;
  }
}

@media (max-width: 430px) {
  .hero__nav-links {
    gap: 9px;
  }

  .hero__visual {
    grid-template-columns: 104px minmax(0, 1fr);
  }

  .hero__stage {
    gap: 28px;
    padding-top: 34px;
  }

  .hero__now strong {
    font-size: 19px;
  }

  .hero__cta .btn {
    flex: 1 1 150px;
  }
}

@media (max-width: 350px) {
  .hero__nav-links > a:nth-child(2) {
    display: none;
  }
}
</style>
